/**
 * Favicons das fontes (redes sociais, sites e assistentes), guardados no projeto em
 * src/assets/favicons/<domínio>.png|svg: os 19 primeiros (17/10/2026) e, em 18/10/2026, os de
 * todos os sites oferecidos na busca (config/content.json, brazil.json e phones.json do backend),
 * em PNG de até 64 px; em 22/10/2026, os 53 que faltavam no catálogo do banco (source_catalog),
 * inclusive as 34 fontes novas; em 30/10/2026, as 12 novas de link na bio, mais 6 (Luma, ZAP,
 * Chaves na Mão, InfoJobs, Dietbox e PsyMeet) e as 85 da pesquisa em outros países. Assim o
 * painel não depende de serviço externo para mostrar as marcas.
 * No build, cada favicon é um arquivo à parte (vite.config.ts): só baixa o que aparece na tela.
 *
 * `faviconFor("br.linkedin.com")` cai no linkedin.com; domínio sem arquivo devolve undefined e
 * quem mostra usa a inicial no lugar.
 */
const FILES = import.meta.glob('../assets/favicons/*.{png,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const BY_DOMAIN: Record<string, string> = Object.fromEntries(
  Object.entries(FILES).map(([path, url]) => [
    path.replace(/^.*\//, '').replace(/\.(png|svg)$/, ''),
    url,
  ]),
);

/** Mesmo arquivo para domínios que são a mesma marca. */
const ALIASES: Record<string, string> = {
  'fb.com': 'facebook.com',
  'm.facebook.com': 'facebook.com',
  'youtu.be': 'youtube.com',
  'wa.me': 'whatsapp.com',
  'web.whatsapp.com': 'whatsapp.com',
  'api.whatsapp.com': 'whatsapp.com',
  'doctoralia.com': 'doctoralia.com.br',
  'linktree.com': 'linktr.ee',
  'olx.com.ar': 'olx.com.br',
  'olx.com.co': 'olx.com.br',
  'olx.com.ec': 'olx.com.br',
};

function cleanDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '');
}

export function faviconFor(value: string | undefined): string | undefined {
  if (!value) return undefined;
  let domain = cleanDomain(value);
  while (domain.includes('.')) {
    const key = ALIASES[domain] ?? domain;
    const hit = BY_DOMAIN[key];
    if (hit) return hit;
    domain = domain.slice(domain.indexOf('.') + 1);
  }
  return undefined;
}
